import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { useThemeOptional } from '../../theme/ThemeProvider.js';
import { getClassroomThemeKey } from '../../theme/themeStorage.js';
import type { ClassroomDto } from '../../api/types.js';
import styles from './ClassroomCard.module.css';

export interface ClassroomCardProps {
  classroom: ClassroomDto;
  /**
   * Drill-in handler. The card's primary tap opens the classroom-detail
   * screen with the given id; the secondary "Activate" pill also sets
   * the selected id before activating, so back-from-live drops on the
   * classroom's detail page rather than the hub list.
   */
  onSelect?: (classroomId: string) => void;
}

export function ClassroomCard({ classroom, onSelect }: ClassroomCardProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { open } = useActiveClassroom();
  const theme = useThemeOptional();

  // Per-classroom mascot preview — pulled from the registry using the
  // classroom's stored theme so the card hints at what the live view
  // will look like. Soft-fails when no ThemeProvider is in scope
  // (test fixtures, pre-theme contexts).
  const storedThemeKey = theme ? getClassroomThemeKey(classroom.id, theme.manifest.key) : null;
  const classroomThemeKey =
    theme && storedThemeKey && theme.isThemeAllowed(storedThemeKey)
      ? storedThemeKey
      : theme?.manifest.key;
  const previewPack =
    theme && classroomThemeKey ? (theme.registry.get(classroomThemeKey) ?? theme.pack) : null;
  const previewMascot = previewPack?.getMascot('home') ?? null;
  const previewBg = previewPack?.getBackground('home') ?? null;
  const previewThemeName = previewPack?.manifest.name ?? '';

  const archive = useMutation({
    mutationFn: () => api.classrooms.archive(classroom.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['classrooms'] }),
  });

  const unarchive = useMutation({
    mutationFn: () => api.classrooms.unarchive(classroom.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['classrooms'] }),
  });

  const isArchived = classroom.status === 'archived';

  const primaryAction = () => {
    if (onSelect) onSelect(classroom.id);
    else open(classroom);
  };

  // Quick-activate from card: also tag this classroom as "selected" via
  // onSelect so PhaseRouter knows to drop the user on detail when they
  // press back from live mode.
  const quickActivate = () => {
    onSelect?.(classroom.id);
    open(classroom);
  };

  return (
    <article
      className={`glass glass--solid ${styles.card} ${previewBg ? styles.cardThemed : ''} ${
        isArchived ? styles.cardArchived : ''
      }`}
      data-testid="classroom-card"
      data-classroom-id={classroom.id}
    >
      {previewBg ? (
        <>
          {/* The classroom's live theme art, behind a scrim that keeps the
           * name + meta readable no matter how bright/busy the image is. */}
          <div
            className={styles.bgLayer}
            style={{ backgroundImage: `url(${previewBg})` }}
            aria-hidden="true"
          />
          <div className={styles.scrim} aria-hidden="true" />
        </>
      ) : null}
      <button
        type="button"
        className={styles.cardButton}
        onClick={primaryAction}
        data-testid="classroom-open-button"
        aria-label={`Open ${classroom.name}`}
        disabled={isArchived}
      >
        <div className={styles.cardText}>
          <span className={styles.cardName}>{classroom.name}</span>
          {classroom.subject || classroom.gradeLevel ? (
            <span className={styles.cardMeta}>
              {[classroom.subject, classroom.gradeLevel].filter(Boolean).join(' · ')}
            </span>
          ) : null}
          <span className={styles.cardYear}>{classroom.schoolYear}</span>
        </div>
        {previewMascot ? (
          <img
            className={styles.cardMascot}
            src={previewMascot}
            alt=""
            draggable={false}
            title={previewThemeName}
          />
        ) : null}
      </button>
      <footer className={styles.cardFooter}>
        {!isArchived ? (
          <button
            type="button"
            className={styles.activateButton}
            onClick={quickActivate}
            data-testid="classroom-activate-quick"
          >
            Activate
          </button>
        ) : null}
        {isArchived ? (
          <button
            type="button"
            className={styles.archiveButton}
            onClick={() => unarchive.mutate()}
            disabled={unarchive.isPending}
            data-testid="classroom-unarchive-button"
          >
            {unarchive.isPending ? 'Unarchiving…' : 'Unarchive'}
          </button>
        ) : (
          <button
            type="button"
            className={styles.archiveButton}
            onClick={() => archive.mutate()}
            disabled={archive.isPending}
            data-testid="classroom-archive-button"
          >
            {archive.isPending ? 'Archiving…' : 'Archive'}
          </button>
        )}
      </footer>
    </article>
  );
}
